import {BasicButton} from '@packages/ui';
import Image from 'next/image';
import type React from 'react';
import {MdIosShare} from 'react-icons/md';
import type {
  RecruitmentDetailSchedule,
  RecruitmentTag as RecruitmentTagType,
} from '../../../../../core/entities/recruitment/schema';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {ShareButton} from '../../../../../shared/components/ShareButton';
import {FollowCompanyButton} from '../../../../company/components/FollowButton';
import {Schedules} from '../../../../recruitingInfo/components/Container/Presenter/Schedules';
import {
  type RecruitmentType,
  Tags,
} from '../../../../recruitingInfo/components/Container/Presenter/Tags';
import {CompanyContentListMenu} from './CompanyContentList';
import {UpdatedBy} from './UpdatedAt';

type Props = {
  company: {
    id: number;
    name: string;
    industry: string;
    logoURL: string;
    isFollowed: boolean;
    followerCount: number;
    isLoading: boolean;
  };
  hasReport: boolean;
  title: string;
  recruitmentType: RecruitmentType;
  targetYear: number;
  groupedJobTypes: Array<{id: number; name: string}>;
  tags: RecruitmentTagType[];
  entryURL?: string;
  schedules?: RecruitmentDetailSchedule[];
  modifiedAt: string;
  shareTitle: string;
  shareUrl: string;
};

export const RecruitmentHeaderSP: React.FC<Props> = ({
  company,
  hasReport,
  title,
  recruitmentType,
  targetYear,
  groupedJobTypes,
  tags,
  entryURL,
  schedules,
  modifiedAt,
  shareTitle,
  shareUrl,
}) => {
  return (
    <div className="flex flex-col bg-white p-24 rounded-b-16 w-full gap-24">
      <div className="flex items-center gap-16">
        {company.logoURL && (
          <CommonLink href={`/company/${company.id}`}>
            <Image
              className="h-[62px] w-[90px] bg-white object-contain relative cursor-pointer mb-4"
              src={company.logoURL}
              alt={title}
              width={62}
              height={90}
            />
          </CommonLink>
        )}
        <div className="flex flex-col">
          {company.name && (
            <h2 className="text-lg leading font-bold">{company.name}</h2>
          )}
          {company.industry && (
            <div className="text-black-3 text-base">{company.industry}</div>
          )}
        </div>
        <ShareButton
          title={shareTitle}
          url={shareUrl}
          variant="white"
          className="flex items-center justify-center bg-black-5 rounded-full w-[36px] h-[36px] pc:w-[44px] pc:h-[44px] hover:opacity-70"
        >
          <MdIosShare size={24} color="#000" />
        </ShareButton>
      </div>
      <div className="flex justify-between">
        <CompanyContentListMenu companyId={company.id} hasReport={hasReport} />
        {!company.isLoading && (
          <FollowCompanyButton
            companyId={company.id}
            initial={{
              isFollowed: company.isFollowed,
              followerCount: 0,
            }}
            widthClass="w-[120px]"
            hasPlus
            hasCount={false}
          />
        )}
      </div>
      <div className="flex flex-col gap-8">
        <div className="flex justify-start items-start flex-wrap mb-4">
          <Tags
            tags={tags}
            type={recruitmentType}
            targetYear={targetYear}
            groupedJobTypes={groupedJobTypes}
            size="sm"
            isGrayColor
          />
        </div>
        <h1 className="leading font-bold">{title}</h1>
        <div className="flex justify-between gap-8">
          <UpdatedBy updatedAt={modifiedAt} />
          {entryURL && (
            <div className="flex justify-end flex-wrap mb-4">
              <a
                href={entryURL}
                target="_blank"
                rel="noopener noreferrer"
                id="recruiting_info_entry_button"
              >
                <BasicButton
                  label="エントリー"
                  className="px-24 py-8 bg-gradient-to-r from-error to-sub text-sm font-bold rounded-[8px] hover:opacity-80"
                  isPrimary={false}
                  isLarge={false}
                  isDisabled={false}
                />
              </a>
            </div>
          )}
        </div>
      </div>
      {schedules && schedules.length > 0 && (
        <div className="flex flex-col gap-8">
          <Schedules
            schedules={schedules}
            withRegisterButton
            withDeadlineText
            buttonType="textWithBorder"
          />
        </div>
      )}
    </div>
  );
};
